/**
 * Recovery for the stale-chunk-after-deploy crash (ADO Bug 858075).
 *
 * A tab left open across a deploy still references the previous build's hashed chunk
 * filenames. When a React.lazy() import for one of those chunks fails, Vite fires
 * `vite:preloadError` on `window` (https://v6.vite.dev/guide/build#load-error-handling) with
 * the original error on `event.payload`; calling `event.preventDefault()` suppresses the
 * throw that would otherwise reach the root TelemetryErrorBoundary. A reload fetches the
 * current `index.html` and the correct chunk hashes, resolving the crash with no visible
 * error screen.
 *
 * A per-session attempt budget guards against a chunk that is genuinely, persistently
 * broken (not just stale): at most MAX_ATTEMPTS_PER_WINDOW reloads within a REARM_WINDOW_MS
 * window anchored to the first attempt (tracked in sessionStorage, self-expiring — no
 * background timer needed; the window does not slide forward on each attempt, it only
 * resets once it has fully expired). Once the budget is exhausted, or sessionStorage itself
 * is unavailable (e.g. privacy mode) or a write to it silently fails to persist, the handler
 * backs off and lets the existing TelemetryErrorBoundary fallback handle it exactly as it
 * did before this fix.
 *
 * An in-memory guard also short-circuits every event after the first one that actually
 * schedules a reload: a single deploy-staleness incident can fire `vite:preloadError` for
 * more than one chunk in the same page load (e.g. a page that lazy-loads several modules on
 * mount), and without this guard each of those would independently consume a budget slot for
 * what is really one incident.
 *
 * Deliberately does NOT check for unsaved user edits before reloading: App.tsx wraps the
 * whole tree in exactly one root TelemetryErrorBoundary with no Suspense fallback below it,
 * so a failed lazy import that isn't recovered here still unmounts the entire app via the
 * crash screen either way — reloading is never a worse outcome for the user than falling
 * through, even when edits would be lost, so there is nothing to gain by skipping it.
 */
import {
  addDatadogError,
  addDatadogLog,
  normalizeErrorForTelemetry,
} from '../services/telemetry/datadog';

export const CHUNK_RELOAD_ATTEMPTED_KEY = 'b2o:chunkReloadAttempted';

const REARM_WINDOW_MS = 10 * 60 * 1000;
const MAX_ATTEMPTS_PER_WINDOW = 2;

export type VitePreloadErrorEvent = Event & { payload?: unknown };

type ReloadAttemptRecord = { count: number; windowStartedAt: number };

type ReloadOutcome = 'reloaded' | 'budget-exceeded' | 'storage-unavailable';

const isReloadAttemptRecord = (value: unknown): value is ReloadAttemptRecord =>
  typeof value === 'object' &&
  value != null &&
  typeof (value as ReloadAttemptRecord).count === 'number' &&
  typeof (value as ReloadAttemptRecord).windowStartedAt === 'number';

/**
 * Reads the current attempt record. `available: false` means sessionStorage ITSELF threw on
 * read (privacy mode) — distinct from `record: null`, which means no prior attempt is on
 * record yet, or the stored value was unrecognized-shaped or not valid JSON at all. A
 * malformed value is sessionStorage working fine with unusable content in this one key, not
 * sessionStorage being unavailable, so it must not be conflated with the `available: false`
 * case above it — that would silently disable the whole recovery mechanism for the rest of
 * the session over a single corrupted value, instead of just resetting to a fresh window.
 */
const readRecord = (): { record: ReloadAttemptRecord | null; available: boolean } => {
  let raw: string | null;
  try {
    raw = sessionStorage.getItem(CHUNK_RELOAD_ATTEMPTED_KEY);
  } catch {
    return { record: null, available: false };
  }

  if (raw == null) {
    return { record: null, available: true };
  }

  try {
    const parsed: unknown = JSON.parse(raw);
    return { record: isReloadAttemptRecord(parsed) ? parsed : null, available: true };
  } catch {
    return { record: null, available: true };
  }
};

/**
 * Writes the record and reads it back to confirm the write actually persisted — some
 * environments accept a `setItem` call without throwing yet silently fail to store it.
 */
const writeRecord = (record: ReloadAttemptRecord): boolean => {
  const serialized = JSON.stringify(record);
  try {
    sessionStorage.setItem(CHUNK_RELOAD_ATTEMPTED_KEY, serialized);
    return sessionStorage.getItem(CHUNK_RELOAD_ATTEMPTED_KEY) === serialized;
  } catch {
    return false;
  }
};

const extractFailedModuleUrl = (payload: unknown): string | undefined => {
  const message =
    payload instanceof Error
      ? payload.message
      : typeof payload === 'object' &&
          payload != null &&
          typeof (payload as { message?: unknown }).message === 'string'
        ? (payload as { message: string }).message
        : undefined;
  if (message == null) {
    return undefined;
  }
  return /dynamically imported module:\s*(\S+)/i.exec(message)?.[1];
};

const reportOutcome = (
  event: VitePreloadErrorEvent,
  outcome: ReloadOutcome,
  // `undefined` means no attempt count could be determined at all (sessionStorage itself
  // threw on read) — kept distinct from a real count (including 0-adjacent small numbers)
  // so a telemetry dashboard grouping/averaging by `reloadAttempt` for this outcome can't
  // mix "attempt N failed to persist" with "we never got to compute an attempt".
  attempt: number | undefined,
): void => {
  const failedModule = extractFailedModuleUrl(event.payload);

  if (outcome === 'reloaded') {
    addDatadogLog('warn', 'Stale chunk detected after deploy — reloading to recover', {
      source: 'chunkReloadRecovery.handleVitePreloadError',
      reloadOutcome: outcome,
      reloadAttempt: attempt,
      failedModule,
    });
    return;
  }

  const genericMessage =
    outcome === 'budget-exceeded'
      ? 'Stale chunk failed to load repeatedly — attempt budget exhausted'
      : 'Stale chunk failed to load and sessionStorage is unavailable to guard a retry';

  const { error: safeError, context } = normalizeErrorForTelemetry(event.payload, {
    genericMessage,
  });
  addDatadogError(safeError, {
    source: 'chunkReloadRecovery.handleVitePreloadError',
    reloadOutcome: outcome,
    reloadAttempt: attempt,
    failedModule,
    ...context,
  });
};

// In-memory (module-level, not persisted) guard: once a reload has been scheduled for this
// page load, every further `vite:preloadError` event this page load is suppressed WITHOUT
// consuming another budget slot or emitting telemetry — but still needs its OWN
// `preventDefault()` (see below). A single deploy-staleness incident can fire this event for
// more than one chunk before the reload actually navigates away (window.location.reload()
// does not halt synchronous execution), and without this guard each one would independently
// consume a budget slot for what is really one incident.
let hasScheduledReloadThisPageLoad = false;

/** Test-only: resets the in-memory reload guard between test cases. Never call from
 * application code — mirrors `__resetDatadogForTests` in `services/telemetry/datadog.ts`. */
export const __resetChunkReloadGuardForTests = (): void => {
  hasScheduledReloadThisPageLoad = false;
};

export const handleVitePreloadError = (event: VitePreloadErrorEvent): void => {
  if (hasScheduledReloadThisPageLoad) {
    // Vite fires a separate, independently cancelable event per failed chunk and still
    // throws unless THIS event is cancelled — the reload already scheduled by an earlier
    // event in this same incident does not suppress it. Without this, an uncancelled second
    // event can still reach the root TelemetryErrorBoundary and paint the crash screen for a
    // moment before the pending reload navigates away, which is exactly the user-visible
    // failure this fix exists to prevent.
    event.preventDefault();
    return;
  }

  const { record, available } = readRecord();

  if (!available) {
    reportOutcome(event, 'storage-unavailable', undefined);
    return;
  }

  const now = Date.now();
  const withinWindow = record != null && now - record.windowStartedAt < REARM_WINDOW_MS;
  const attempt = withinWindow ? record.count + 1 : 1;
  const windowStartedAt = withinWindow ? record.windowStartedAt : now;

  if (attempt > MAX_ATTEMPTS_PER_WINDOW) {
    reportOutcome(event, 'budget-exceeded', attempt);
    return;
  }

  if (!writeRecord({ count: attempt, windowStartedAt })) {
    reportOutcome(event, 'storage-unavailable', attempt);
    return;
  }

  hasScheduledReloadThisPageLoad = true;
  event.preventDefault();
  reportOutcome(event, 'reloaded', attempt);
  window.location.reload();
};

/** Call once, before the app renders, to arm the recovery mechanism for the page's lifetime. */
export const registerChunkReloadRecovery = (): void => {
  window.addEventListener('vite:preloadError', handleVitePreloadError);
};
